/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

import styled from 'styled-components';
import MDEditor from '@uiw/react-md-editor';
import remarkGfm from 'remark-gfm';

import { LayoutDocumentation } from '@common/layout';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '@store/index';

import { useLocation } from 'react-router-dom';
import { Alert, alpha } from '@mui/material';
import { getDocumentation } from '@requests/documentation';
import { useQuery } from '@tanstack/react-query';
import { documentationActions } from '@store/slices/documentation';

import { DocsHeaders } from './DocsHeaders';
import { Loader } from '@common/hooks/useLoader';
import { DocsNavigation, NavItem } from './DocsNavigation';
import { DocsSections } from './types';

export const SIDENAV_WIDTH = '260px';
export const SIDENAV_WIDTH_PADDED = '340px';
export const NAVBAR_TOP_HEIGHT = '89px';

const MARKDOWN_WIDTH = `calc(100vw - ${SIDENAV_WIDTH_PADDED} - 61px - 95px)`;
const MAX_MARKDOWN_WIDTH = `min(${MARKDOWN_WIDTH}, 850px)`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;

  .wmde-markdown,
  .w-md-editor,
  .w-md-editor-toolbar {
    background-color: ${({ theme }) => theme.background.standard};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.text.standard} !important;
    border-color: ${({ theme }) => theme.line.light} !important;
  }

  p,
  ul,
  li {
    color: ${({ theme }) => alpha(theme.text.standard, 0.8)};
  }

  .w-md-editor {
    --md-editor-box-shadow-color: ${({ theme }) => theme.line.light};
  }

  hr {
    border: 0 !important;
    height: 1px !important;
    background-color: ${({ theme }) => theme.line.light} !important;
  }

  video {
    max-width: ${MAX_MARKDOWN_WIDTH};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.line.light};
  }

  img {
    max-width: ${MAX_MARKDOWN_WIDTH};
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.line.light};
    max-height: 500px;
  }

  h1:not(:first-child),
  h2:not(:first-child) {
    margin-top: 3rem;
  }

  h3:not(:first-child),
  h4:not(:first-child) {
    padding-top: 1rem;
  }

  p img {
    display: block;
    margin-left: auto;
    margin-right: auto;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.04);
  }

  a.anchor {
    svg {
      color: ${({ theme }) => theme.text.standard};
    }
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    background: ${({ theme }) => theme.background.standard} !important;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background: ${({ theme }) => theme.text.subtext};
  }
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
  overflow-y: auto;
  padding-top: 24px;
`;

const ContainerWithFooter = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const MarkdownPreview = styled(MDEditor.Markdown)`
  max-width: ${MAX_MARKDOWN_WIDTH};
  min-width: ${MAX_MARKDOWN_WIDTH};

  padding-bottom: 2.5rem;
  margin: 0 28px;
  margin-right: 58px;

  img:hover {
    cursor: zoom-in;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  padding-top: 2rem;
  justify-content: center;
`;

export const FullscreenImageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000; // Ensure this is above your other content

  img {
    background-color: transparent !important;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.line.light};
  }

  img:hover {
    cursor: zoom-out;
  }
`;

export const FullscreenImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  transition: transform 0.5s ease;
`;

const scrollToTop = () => {
  // Reset window scroll
  window.scrollTo({ top: 0, behavior: 'instant' });

  // Reset all scrollable containers
  const scrollableElements = document.querySelectorAll('*');
  scrollableElements.forEach((element) => {
    if (element instanceof HTMLElement) {
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll';

      if (hasScroll && element.scrollTop > 0) {
        element.scrollTop = 0;
      }
    }
  });
};

export const Docs: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const activeTab = useSelector((state: State) => state.documentation.activeTab);

  const {
    data: documentation = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['docs'],
    queryFn: getDocumentation,
  });

  let errorMessage = '';
  if (axios.isAxiosError(error)) errorMessage = error.response?.data?.error || error.message;
  else errorMessage = error?.message || '';

  const [value, setValue] = useState('');

  // Get all subsections in a flat array
  const allSubsections = documentation
    .map((route) => route.subsections)
    .flat()
    .filter((item) => !item.dividor);

  // Find current route
  const currentIndex = allSubsections.findIndex((link) => link.href === location.pathname);
  const route = currentIndex !== -1 ? allSubsections[currentIndex] : undefined;

  const getArticle = (type: 'next' | 'previous'): NavItem | undefined => {
    if (type === 'next') {
      const hasNextArticle = currentIndex !== -1 && currentIndex < allSubsections.length - 1;
      if (!hasNextArticle) return undefined;

      return {
        href: allSubsections[currentIndex + 1].href || '',
        name: allSubsections[currentIndex + 1].name,
      };
    }

    if (type === 'previous') {
      const hasPreviousArticle = currentIndex > 0;
      if (!hasPreviousArticle) return undefined;
      return {
        href: allSubsections[currentIndex - 1].href || '',
        name: allSubsections[currentIndex - 1].name,
      };
    }
  };

  // Find previous and next articles
  const previousArticle = getArticle('previous');
  const nextArticle = getArticle('next');

  const doc = documentation.find((doc) => doc.id === route?.section_id);
  const mkDocument = doc?.subsections.find((link) => link.id === activeTab?.subsectionId);

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  useEffect(() => {
    const pathname = location.pathname;

    if (!documentation.length) {
      return;
    }

    // Flatten all subsections into a single array for easier searching
    const allSubsections = documentation.reduce((acc, section) => {
      return acc.concat(
        section.subsections.map((subsection) => ({
          ...subsection,
          sectionName: section.name,
        }))
      );
    }, [] as Array<DocsSections['subsections'][0] & { sectionName: string }>);

    // Find the matching route
    const route = allSubsections.find((subsection) => subsection.href === pathname);

    if (route) {
      if (route.document?.content) {
        setValue(route.document.content);

        dispatch(
          documentationActions.setActiveTab({
            name: route.name,
            href: route.href as string,
            sectionId: route.section_id,
            subsectionId: route.id,
          })
        );

        // Immediately scroll to top when route changes
        scrollToTop();
      }
    }
  }, [documentation, location.pathname]);

  const [fullscreenSrc, setFullscreenSrc] = useState('');

  useEffect(() => {
    const handleClick = (event: any) => {
      if (event.target.tagName === 'IMG') {
        setFullscreenSrc(event.target.src);
      } else setFullscreenSrc('');
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const closeFullscreen = () => {
    setFullscreenSrc('');
  };

  if (error) {
    return (
      <LayoutDocumentation value={value} mkDocument={mkDocument?.document} documentLoading={isLoading}>
        <div style={{ maxWidth: MAX_MARKDOWN_WIDTH }}>
          <Alert sx={{ minWidth: '500px' }} severity='error'>
            {errorMessage || 'Unable to load document'}
          </Alert>
        </div>
      </LayoutDocumentation>
    );
  }

  return (
    <LayoutDocumentation value={value} mkDocument={mkDocument?.document} documentLoading={false}>
      <Container ref={containerRef}>
        {isLoading ? (
          <LoadingContainer>
            <Loader />
          </LoadingContainer>
        ) : (
          <ContentContainer>
            <ContainerWithFooter>
              <MarkdownPreview source={value} remarkPlugins={[remarkGfm]} />
              <DocsNavigation previousArticle={previousArticle} nextArticle={nextArticle} onBackToTop={scrollToTop} />
            </ContainerWithFooter>
            <DocsHeaders mkDocument={value} />
          </ContentContainer>
        )}
      </Container>
      {fullscreenSrc && (
        <FullscreenImageOverlay onClick={closeFullscreen}>
          <FullscreenImage
            src={fullscreenSrc}
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenSrc('');
            }}
            alt='Fullscreen Image'
          />
        </FullscreenImageOverlay>
      )}
    </LayoutDocumentation>
  );
};
